import { ErrorMessage } from "@components/ErrorMessage";
import { fullNameSectionCss } from "@components/Signup/BuildProfileSteps";
import { TextInput, TextInputType } from "@components/TextInput";
import { validateName } from "@components/donate/DonateV3/PaymentProcess/validators";
import { DonateFormType } from "@components/donate/DonateV3/types";
import styled from "@emotion/styled";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormReturn,
} from "react-hook-form";

import { MediaSize, cssForMediaSize } from "src/theme/mediaQueries";
import { verticalStackCss } from "src/theme/spacing";
import { TextSize, textSizeCss } from "src/theme/text";

const StyledTextInput = styled(TextInput)`
  label {
    ${cssForMediaSize({
      min: MediaSize.MEDIUM,
      css: textSizeCss[TextSize.s],
    })}
  }
`;

function getErrors(formErrors: FieldErrors<DonateFormType>) {
  let firstNameErrorMessage = formErrors.firstName?.message;
  let lastNameErrorMessage = formErrors.lastName?.message;

  if (
    formErrors.firstName?.type === "required" &&
    formErrors.lastName?.type === "required"
  ) {
    firstNameErrorMessage = "Please provide a first and last name";
    lastNameErrorMessage = undefined;
  } else if (
    formErrors.firstName?.type === "pattern" &&
    formErrors.lastName?.type === "pattern"
  ) {
    firstNameErrorMessage = "Only latin characters A-Z may be used.";
    lastNameErrorMessage = undefined;
  }

  return { firstNameErrorMessage, lastNameErrorMessage };
}

export const FullNameSection = ({
  control,
  form,
}: {
  control?: Control<DonateFormType>;
  form: Omit<UseFormReturn<DonateFormType>, "handleSubmit">;
}) => {
  const {
    formState: { errors },
  } = form;

  const { firstNameErrorMessage, lastNameErrorMessage } = getErrors(errors);

  return (
    <section css={[verticalStackCss.s]}>
      <div css={fullNameSectionCss}>
        <Controller
          control={control}
          name="firstName"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <StyledTextInput
              type={TextInputType.TEXT}
              id="firstName"
              data-tname="firstName"
              name="firstName"
              labelText="First name"
              autoComplete="given-name"
              value={value}
              onChange={(event) => {
                onChange(event);
              }}
              collapseDescriptionSpace
              onBlur={() =>
                validateName(
                  value,
                  "firstName",
                  "first name",
                  form.setError,
                  form.clearErrors
                )
              }
              validationStatus={
                error && {
                  success: false,
                }
              }
            />
          )}
        />
        <Controller
          control={control}
          name="lastName"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <StyledTextInput
              type={TextInputType.TEXT}
              id="lastName"
              data-tname="lastName"
              name="lastName"
              labelText="Last name"
              autoComplete="family-name"
              value={value}
              onChange={(event) => {
                onChange(event);
              }}
              collapseDescriptionSpace
              onBlur={() =>
                validateName(
                  value,
                  "lastName",
                  "last name",
                  form.setError,
                  form.clearErrors
                )
              }
              validationStatus={
                error && {
                  success: false,
                }
              }
            />
          )}
        />
      </div>
      {firstNameErrorMessage && (
        <ErrorMessage>{firstNameErrorMessage}</ErrorMessage>
      )}
      {lastNameErrorMessage && (
        <ErrorMessage>{lastNameErrorMessage}</ErrorMessage>
      )}
    </section>
  );
};
